/**
 * AIUsageContext
 *
 * Shared context for AI usage tracking. Fetches usage on mount,
 * exposes computed state (percent, warnings), and provides refreshUsage().
 * Follows the SubscriptionContext pattern.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useAuth } from './AuthContext'
import { useSubscription } from './SubscriptionContext'
import { getAIChatUsage } from '@/lib/api/api'
import type { AIUsage } from '@/lib/types/ai'

// ============================================================================
// Context Types
// ============================================================================

interface AIUsageContextType {
  usage: AIUsage | null
  isLoading: boolean
  usagePercent: number
  isApproachingLimit: boolean
  isOverLimit: boolean
  hasAIAccess: boolean
  refreshUsage: () => Promise<void>
}

// ============================================================================
// Context
// ============================================================================

const AIUsageContext = createContext<AIUsageContextType | null>(null)

// ============================================================================
// Provider
// ============================================================================

const WARNING_THRESHOLD = 80

export function AIUsageProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  const { hasFeature } = useSubscription()
  const hasAIAccess = hasFeature('aiCopilot')

  const [usage, setUsage] = useState<AIUsage | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchUsage = useCallback(async () => {
    if (!token) return
    setIsLoading(true)
    try {
      const data = await getAIChatUsage(token)
      if (data?.calls) {
        setUsage(data)
      }
    } catch {
      // Silent fail - usage display is non-critical
    } finally {
      setIsLoading(false)
    }
  }, [token])

  // Fetch on mount / token change
  useEffect(() => {
    if (token && hasAIAccess) {
      fetchUsage()
    }
  }, [token, hasAIAccess, fetchUsage])

  const usagePercent = useMemo(() => {
    if (!usage?.calls?.limit) return 0
    return Math.min(100, (usage.calls.used / usage.calls.limit) * 100)
  }, [usage])

  const isApproachingLimit = usagePercent >= WARNING_THRESHOLD
  const isOverLimit = usage ? usage.calls.remaining <= 0 : false

  const value: AIUsageContextType = useMemo(() => ({
    usage,
    isLoading,
    usagePercent,
    isApproachingLimit,
    isOverLimit,
    hasAIAccess,
    refreshUsage: fetchUsage,
  }), [usage, isLoading, usagePercent, isApproachingLimit, isOverLimit, hasAIAccess, fetchUsage])

  return (
    <AIUsageContext.Provider value={value}>
      {children}
    </AIUsageContext.Provider>
  )
}

// ============================================================================
// Hooks
// ============================================================================

/** Throws if used outside AIUsageProvider */
export function useAIUsage(): AIUsageContextType {
  const context = useContext(AIUsageContext)
  if (!context) {
    throw new Error('useAIUsage must be used within AIUsageProvider')
  }
  return context
}

/** Returns null if used outside AIUsageProvider (safe version) */
export function useAIUsageSafe(): AIUsageContextType | null {
  return useContext(AIUsageContext)
}
